import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth, usePublicConfig } from '../context';
import { normalizePhoneNumber } from '../utils/phone';

const loginSchema = yup
  .object({
    phone: yup.string().required('Phone number is required'),
    password: yup
      .string()
      .min(6, 'Password should be at least 6 characters')
      .required('Password is required'),
    remember: yup.boolean().default(true).required(),
  })
  .required();

type LoginFormValues = yup.InferType<typeof loginSchema>;

export const LoginPage = () => {
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading } = useAuth();
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';
  
  const fromPath = useMemo(() => {
    const state = location.state as { from?: Location } | undefined;
    return state?.from?.pathname ?? '/dashboard';
  }, [location.state]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      phone: '',
      password: '',
      remember: true,
    },
  });

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(fromPath, { replace: true });
    }
  }, [fromPath, isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <section className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-300">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="text-sm font-medium">
            {isAuthenticated ? 'Redirecting you to your dashboard…' : 'Checking your credentials…'}
          </p>
        </div>
      </section>
    );
  }

  const onSubmit = async (values: LoginFormValues) => {
    setFormError(null);

    const normalizedPhone = normalizePhoneNumber(values.phone.trim());

    try {
      await login({
        phone: normalizedPhone,
        password: values.password,
        remember: values.remember,
      });

      reset({ ...values, phone: normalizedPhone, password: '' });
      navigate(fromPath, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed. Please try again.';
      setFormError(message);
    }
  };

  return (
    <section className="relative flex min-h-screen w-full flex-col bg-slate-900 text-slate-100 lg:flex-row">
      <div
        className="fixed inset-x-0 top-0 h-[48vh] w-full overflow-hidden lg:hidden"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-[calc(12vh+env(safe-area-inset-bottom))] pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            {chamaName}
          </span>
          <h1 className="mt-3 font-display text-3xl leading-snug text-white">Welcome back.</h1>
          <p className="mt-2 text-sm leading-relaxed text-brand-100/80">
            Track contributions, confirm payouts, and keep {chamaName} moving in one calm dashboard.
          </p>
        </div>
      </div>

      <div
        className="relative hidden flex-1 items-center justify-center px-16 py-20 lg:flex"
        style={{ backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 max-w-md space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            {chamaName}
          </span>
          <h1 className="font-display text-4xl leading-tight text-white">
            Welcome back.
          </h1>
          <p className="text-base leading-relaxed text-brand-100/70">
            Track contributions, confirm payouts, and keep {chamaName} moving in one calm dashboard. Your
            actions are logged for committee transparency.
          </p>
          <div className="space-y-3 text-sm text-brand-100/80">
            <div className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Need an invite?</p>
              <p className="mt-1">
                Submit a request on the{' '}
                <Link to="/apply" className="font-semibold text-brand-100 underline decoration-brand-100/60">
                  member application page
                </Link>
                .
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3">
              <p className="font-semibold text-white">Support desk</p>
              <p className="mt-1">hello@{chamaName.toLowerCase().replace(' ', '')}.com · +254 700 111 222</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[48vh] flex flex-1 items-end justify-center bg-transparent pb-8 lg:mt-0 lg:min-h-screen lg:items-center lg:bg-white lg:px-8 lg:py-16 lg:text-slate-900 dark:lg:bg-slate-950 dark:lg:text-slate-100">
        <div className="fixed inset-x-0 bottom-0 z-10 w-full rounded-t-[2.5rem] bg-white px-6 py-10 text-slate-900 shadow-2xl dark:bg-slate-950 dark:text-slate-100 max-h-[80vh] overflow-y-auto pb-[calc(16px+env(safe-area-inset-bottom))] lg:static lg:max-w-md lg:rounded-none lg:px-10 lg:py-16 lg:shadow-none lg:max-h-none lg:overflow-visible">
          <div className="mb-10 text-center lg:text-left">
            <h2 className="font-display text-3xl text-slate-900 dark:text-white">Sign in to {chamaName}</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              Use your registered phone number or email together with your password.
            </p>
          </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6"
              noValidate
            >
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="username"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  {...register('phone')}
                />
                {errors.phone && <p className="mt-2 text-sm text-red-500">{errors.phone.message}</p>}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  {...register('password')}
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800"
                    {...register('remember')}
                  />
                  <span>Keep me signed in</span>
                </label>
                <Link
                  to="/reset-password"
                  className="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>

              {formError ? (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  {formError}
                </p>
              ) : null}

              <p className="text-sm text-slate-600 dark:text-slate-300">
                New to {chamaName}?{' '}
                <Link to="/apply" className="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300">
                  Apply to join
                </Link>
              </p>
            </form>
        </div>
      </div>
    </section>
  );
};

